import { Injectable } from '@angular/core';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { ChecklistTemplate } from '@portal-core/checklist-templates/models/checklist-template.model';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { GetDataOptions } from '@portal-core/data/common/models/get-data-options.model';
import { DataService } from '@portal-core/data/common/services/data.service';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { map, Observable, of, switchMap, tap } from 'rxjs';
import { ChecklistTemplatesApiService } from './checklist-templates-api.service';
import { ChecklistTemplatesDataService } from './checklist-templates-data.service';

@Injectable({
  providedIn: 'root'
})
@Resettable()
export class ChecklistTemplatesService extends CollectionServiceBase<ChecklistTemplate> {
  constructor(
    private checklistTemplatesDataService: ChecklistTemplatesDataService,
    private checklistTemplatesApiService: ChecklistTemplatesApiService,
    protected dataService: DataService
  ) {
    super(checklistTemplatesDataService, dataService);
  }

  getChecklistTemplatesPage$(licenseId: number, filter?: PageFilter): Observable<Page<ChecklistTemplate>> {
    return this.checklistTemplatesApiService.getChecklistTemplatesPage$(licenseId, filter);
  }

  getChecklistTemplatesByLicenseId$(licenseId: number, options?: GetDataOptions): Observable<ChecklistTemplate[]> {
    return this.dataService.getDataListItems$<ChecklistTemplate>('Licenses', licenseId, this.checklistTemplatesDataService, {
      fetch: () => this.checklistTemplatesApiService.getChecklistTemplatesByLicenseId$(licenseId).pipe(
        switchMap(templates => {
          if (templates?.length > 0) {
            return this.addItems$(templates).pipe(
              map(() => templates)
            );
          } else {
            return of(templates);
          }
        }),
        map(templates => {
          if (Array.isArray(templates)) {
            return templates.sort((templateA, templateB) => templateA.Name.localeCompare(templateB.Name));
          }
        })
      )
    }, options);
  }

  createChecklistTemplate$(template: ChecklistTemplate): Observable<any> {
    return this.checklistTemplatesApiService.upsertChecklistTemplate$(template).pipe(
      tap(response => {
        this.checklistTemplatesDataService.addItems$([response.Result]);
        this.checklistTemplatesDataService.addItemsToListById$('Licenses', response.Result.LicenseId, [response.Result]);
      })
    );
  }

  updateChecklistTemplate$(template: ChecklistTemplate): Observable<any> {
    return this.checklistTemplatesApiService.upsertChecklistTemplate$(template).pipe(
      tap(response => this.checklistTemplatesDataService.updateItems$([response.Result]))
    );
  }

  deleteChecklistTemplate$(templateId: number): Observable<any> {
    return this.checklistTemplatesApiService.deleteChecklistTemplate$(templateId).pipe(
      tap(() => this.checklistTemplatesDataService.removeItems$([templateId]))
    );
  }

  verifyChecklistTemplateName$(licenseId: number, checklistTemplateName: string): Observable<any> {
    return this.checklistTemplatesApiService.verifyChecklistTemplateName$(licenseId, checklistTemplateName);
  }
}
